import { Color } from 'common';
import Data from 'data';
import Locale from 'data/locale';
import { formatStr } from 'logic/item';
import { skillCalc, skillFormula, d2_addpercent, skillPassiveStatsLazy, skillAuraStatsLazy } from './calc';

const locale = str => Locale.strings[str] ?? str ?? '';

const elemTypes = {
  fire: {
    damage: 'StrSkill5',
  },
  ltng: {
    damage: 'StrSkill7',
  },
  mag: {
    damage: 'StrSkill39',
  },
  cold: {
    damage: 'StrSkill6',
    length: 'StrSkill13',
  },
  pois: {
    damage: 'StrSkill8',
    length: 'StrSkill14',
  },
};

const plus = value => value > 0 ? '+' + value : value;

export default function skillTooltip(id, stats, full, canLevel, level) {
  const skill = Data.skills[id];
  const desc = Data.skillDesc[skill?.skilldesc];
  if (!desc || !stats) return ['\\xffc1Unknown skill'];

  level = level || stats.skills[id] || 0;
  const base_level = stats.base_skills[id] || 0;

  const color = (level || canLevel) ? Color.WHITE : Color.RED;

  const lines = [];
  if (!canLevel && !base_level && full) {
    lines.push(`\\xffc${Color.GREEN}${Locale.strings.StrSkill119}`);
    lines.push('');
  }
  if (level && !base_level && full && canLevel !== 'special') {
    lines.push(`\\xffc${Color.GREEN}${Locale.strings.StrSkill118}`);
    lines.push('');
  }
  lines.push(`\\xffc${Color.GREEN}${locale(desc.strname)}`);
  if (Data.info.legacyFormat) {
    lines.push(...locale(desc.strlong).split('\n').map(line => `\\xffc${color}${line}`).reverse());
  } else {
    lines.push(...locale(desc.strlong).split('\n').map(line => `\\xffc${color}${line}`));
  }
  const reqLevel = skill.reqlevel + base_level;
  if (reqLevel > 1 && (!level || (base_level < 20 && !canLevel))) {
    if (Data.info.legacyFormat) {
      lines.push(`\\xffc${color}${Locale.strings.skilldesc3}${reqLevel}`);
    } else {
      lines.push(`\\xffc${color}${formatStr(Locale.strings.skilldesc3, reqLevel)}`);
    }
  } else if (base_level === 20 && canLevel) {
    lines.push(`\\xffc${color}${Locale.strings.StrSkill116}`);
  }

  function descFunc(func, texta, textb, calca, calcb, level) {
    switch (func) {
    case 1:
      {
        const mana = skillFormula(skill, 'usmc', stats, level);
        let prec = skillCalc(skill, calca, stats, level);
        if (!prec && ((skill.mana << skill.manashift) & 255)) prec = 10;
        else if (!prec) prec = 1;
        return mana && `${locale(desc.strmana)}${((mana * prec) >> 8) / prec}`;
      }
    case 2:
      {
        const value = skillCalc(skill, calca, stats, level);
        return value && `${locale(texta)}${plus(value)}${locale(textb)}`;
      }
    case 3:
      {
        const value = skillCalc(skill, calca, stats, level);
        return value && `${locale(texta)}${value}${locale(textb)}`;
      }
    case 4:
      {
        const value = skillCalc(skill, calca, stats, level);
        return value && `${locale(texta)}${plus(value)}`;
      }
    case 5:
      {
        const value = skillCalc(skill, calca, stats, level);
        return value && `${locale(texta)}${value}`;
      }
    case 6:
      {
        const value = skillCalc(skill, calca, stats, level);
        return value && `${plus(value)}${locale(texta)}`;
      }
    case 7:
      {
        const value = skillCalc(skill, calca, stats, level);
        return value && `${value}${locale(texta)}`;
      }
    case 8:
      {
        const tohit = skillFormula(skill, 'toht', stats, level);
        return tohit && `${Locale.strings.StrSkill10}${plus(tohit)}${Locale.strings.StrSkill23}`;;
      }
    case 9:
      {
        let min = skillFormula(skill, 'pdns', stats, level);
        let max = skillFormula(skill, 'pdxs', stats, level);
        const pct = skillCalc(skill, calca, stats, level);
        min = (min + min * pct / 100) >> 8;
        max = (max + max * pct / 100) >> 8;
        if (min < max) {
          return `${Locale.strings.StrSkill4}${min}-${max}`;
        } else {
          return min && `${Locale.strings.StrSkill4}+${min}`;
        }
      }
    case 10:
    case 48:
      {
        const label = elemTypes[skill.etype]?.damage;
        if (!label) return null;
        const min = skillFormula(skill, 'enma', stats, level);
        const max = skillFormula(skill, 'exma', stats, level);
        if (min < max) {
          return `${locale(label)}${min}-${max}`;
        } else {
          return min && `${locale(label)}${plus(min)}`;
        }
      }
    case 11:
      {
        const label = elemTypes[skill.etype]?.length;
        if (!label) return null;
        const len = skillFormula(skill, 'edma', stats, level);
        return len && `${locale(label)}${((len * 10 / 25) | 0) / 10}${Locale.strings.StrSkill16}`;
      }
    case 12:
      {
        const len = (skillCalc(skill, calca, stats, level) * 10 / 25) | 0;
        return `${locale(textb)}${locale(texta)}${len / 10}${len === 10 ? Locale.strings.StrSkill15 : Locale.strings.StrSkill16}`;
      }
    case 13:
      {
        const monster = Data.monsters[skill.summon.toLowerCase()];
        if (!monster) return null;
        const percent = skillCalc(skill, calca, stats, level);
        const suffix = ['', 'n', 'h'][stats.difficulty || 0];
        const base = (monster['minhp' + suffix] + monster['maxhp' + suffix]) * 128;
        const result = (d2_addpercent(base, percent) >> 8) + skillCalc(skill, calcb, stats, level);
        if (Data.info.legacyFormat) {
          return `${Locale.strings.StrSkill42}${result}`;
        } else {
          return formatStr(Locale.strings.StrSkill42, result);
        }
      }
    case 14:
      {
        const label = elemTypes[skill.etype]?.damage;
        if (!label) return null;
        const len = skillFormula(skill, 'edma', stats, level);
        const min = (skillFormula(skill, 'enms', stats, level) * len) >> 8;
        const max = (skillFormula(skill, 'exms', stats, level) * len) >> 8;
        const ilen = (len * 10 / 25) | 0;
        const duration = `${Locale.strings.StrSkill63}${ilen / 10}${ilen === 10 ? Locale.strings.StrSkill15 : Locale.strings.StrSkill16}`;
        if (min < max) {
          return `${locale(label)}${min}-${max}\n${duration}`;
        } else {
          return min && `${locale(label)}${min}\n${duration}`;
        }
      }
    case 15: return `${locale(texta)}:${locale(textb)}`;
    case 16: return `${locale(textb)}${locale(texta)}${((skillCalc(skill, calca, stats, level) * 10 / 25) | 0) / 10}-${((skillCalc(skill, calcb, stats, level) * 10 / 25) | 0) / 10}${Locale.strings.StrSkill16}`;
    case 17: return `${locale(textb)}${locale(texta)}${skillCalc(skill, calca, stats, level)}-${skillCalc(skill, calcb, stats, level)}${Locale.strings.StrSkill34}`;
    case 18: return locale(texta);
    case 19:
      {
        const radius = (skillCalc(skill, calca, stats, level) * 20 / 3) | 0;
        return `${locale(textb)}${locale(texta)}${radius / 10}${radius === 10 ? Locale.strings.StrSkill36 : Locale.strings.StrSkill26}`;
      }
    case 20: return `${locale(texta)}+${skillCalc(skill, calca, stats, level)}${Locale.strings.StrSkill23}${locale(textb)}`;
    case 21: return `${locale(texta)}${skillCalc(skill, calca, stats, level)}${Locale.strings.StrSkill23}${locale(textb)}`;
    case 22:
      {
        const label = elemTypes[skill.etype]?.damage;
        if (!label) return null;
        const min = (skillFormula(skill, 'm1nm', stats, level) * 3 * 25) >> 8;
        const max = (skillFormula(skill, 'm1xm', stats, level) * 3 * 25) >> 8;
        if (min < max) {
          return `${Locale.strings.StrSkill68}${locale(label)}${min}-${max}${Locale.strings.StrSkill34}`;
        } else {
          return min && `${Locale.strings.StrSkill68}${locale(label)}${min}${Locale.strings.StrSkill34}`;
        }
      }
    case 23:
      {
        const len = (skillFormula(skill, 'm1rn', stats, level) * 10 / 25) | 0;
        return `${locale(textb)}${locale(texta)}${len / 10}${len === 10 ? Locale.strings.StrSkill15 : Locale.strings.StrSkill16}`;
      }
    case 24: return `${locale(textb)}${locale(texta)}${skillFormula(skill, 'enma', stats, level)}-${skillFormula(skill, 'exma', stats, level)}`;
    case 25: return locale(texta) + locale(textb);
    case 26:
      {
        const label = elemTypes[skill.etype]?.damage;
        if (!label) return null;
        const min = (skillFormula(skill, 'enms', stats, level) * 25) >> 8;
        const max = (skillFormula(skill, 'exms', stats, level) * 25) >> 8;
        if (min < max) {
          return `${Locale.strings.StrSkill68}${locale(label)}${min}-${max}${Locale.strings.StrSkill34}`;
        } else {
          return min && `${Locale.strings.StrSkill68}${locale(label)}${min}${Locale.strings.StrSkill34}`;
        }
      }
    case 27:
      {
        const label = elemTypes[skill.etype]?.damage;
        if (!label) return null;
        const min = (skillFormula(skill, 'enms', stats, level) * 3 * 25) >> 8;
        const max = (skillFormula(skill, 'exms', stats, level) * 3 * 25) >> 8;
        if (min < max) {
          return `${Locale.strings.StrSkill68}${locale(label)}${min}-${max}${Locale.strings.StrSkill34}`;
        } else {
          return min && `${Locale.strings.StrSkill68}${locale(label)}${min}${Locale.strings.StrSkill34}`;
        }
      }
    case 28:
      {
        const radius = skillFormula(skill, 'm1sz', stats, level);
        return `${Locale.strings.StrSkill18}${radius}${radius === 1 ? Locale.strings.StrSkill36 : Locale.strings.StrSkill26}`;
      }
    case 29:
      {
        const range = (skillFormula(skill, 'm1rn', stats, level) * 2 / 3) | 0;
        return `${range}${range === 1 ? Locale.strings.StrSkill36 : Locale.strings.StrSkill26}`;
      }
    case 30:
      {
        const len = (skillFormula(skill, 'mxrn', stats, level) * 10 / 25) | 0;
        return `${locale(textb)}${locale(texta)}${len / 10}${len === 10 ? Locale.strings.StrSkill15 : Locale.strings.StrSkill16}`;
      }
    case 31:
      const divisor = Data.difficulty[stats.difficulty || 0]?.aicursedivisor || 1;
      if (Data.info.legacyFormat) {
        const len = (((skillCalc(skill, calca, stats, level) / divisor) | 0) * 10 / 25) | 0;
        return `${locale(textb)}${locale(texta)}${len / 10}${len === 10 ? Locale.strings.StrSkill15 : Locale.strings.StrSkill16}`;
      } else {
        const len = (((skillCalc(skill, calca, stats, level) / divisor) | 0) * 10 / skillCalc(skill, calcb, stats, level)) | 0;
        return formatStr(locale((len !== 10 && textb) || texta), len / 10);
      }
    case 32: return `${locale(texta)}${locale(textb)}${plus(skillCalc(skill, calca, stats, level))}${Locale.strings.StrSkill23}`;
    case 33: return locale(texta) + locale(textb);
    case 34:
    case 39:
      {
        const monster = Data.monsters[skill.summon.toLowerCase()];
        if (!monster) return null;
        const suffix = ['', 'n', 'h'][stats.difficulty || 0];
        let mindam = monster['a1mind' + suffix];
        let maxdam = monster['a1maxd' + suffix];
        const passives = skillPassiveStatsLazy(skill, stats, level);
        const auras = skillAuraStatsLazy(skill, stats, level);
        if (passives.item_normaldamage) {
          const value = passives.item_normaldamage();
          mindam += value;
          maxdam += value;
        }
        if (auras.damagepercent || passives.damagepercent) {
          const value = (auras.damagepercent?.() || 0) + (passives.damagepercent?.() || 0);
          mindam += (mindam * value / 100) | 0;
          maxdam += (maxdam * value / 100) | 0;
        }
        if (Data.info.legacyFormat) {
          if (mindam < maxdam) {
            return `${Locale.strings.StrSkill4}${mindam}-${maxdam}`;
          } else {
            return mindam && `${Locale.strings.StrSkill4}${mindam}`;
          }
        } else {
          return formatStr(locale(texta), mindam, maxdam);
        }
      }
    case 35: return `${locale(texta)}: ${skillCalc(skill, calca, stats, level)}-${skillCalc(skill, calcb, stats, level)}`;
    case 36:
      if (Data.info.legacyFormat) {
        const value = skillCalc(skill, calca, stats, level);
        return `${value}${value > 1 ? locale(textb) : locale(texta)}`;
      } else if (calcb) {
        const value = (skillCalc(skill, calca, stats, level) * 10 / skillCalc(skill, calcb, stats, level)) | 0;
        return formatStr(locale((value !== 10 && textb) || texta), value / 10);
      } else {
        const value = skillCalc(skill, calca, stats, level);
        return formatStr(locale((value !== 1 && textb) || texta), value);
      }
    case 37:
      {
        const radius = (skillCalc(skill, calca, stats, level) * 10 / 3) | 0;
        return `${locale(textb)}${locale(texta)}${radius / 10}${radius === 10 ? Locale.strings.StrSkill36 : Locale.strings.StrSkill26}`;
      }
    case 38: return `${locale(texta)}${skillCalc(skill, calca, stats, level)}-${skillCalc(skill, calcb, stats, level)}${locale(textb)}`;
    case 40: return `\\xffc${calca}${formatStr(locale(texta), locale(textb))}`;
    case 41:
      {
        const monster = Data.monsters[skill.summon.toLowerCase()];
        if (!monster) return null;
        const label = elemTypes[skill.etype]?.damage;
        if (!label) return null;
        const suffix = ['', 'n', 'h'][stats.difficulty || 0];
        const mindam = monster['a1mind' + suffix] + skillCalc(skill, calca, stats, level);
        const maxdam = monster['a1maxd' + suffix] + skillCalc(skill, calcb, stats, level);
        if (Data.info.legacyFormat) {
          if (mindam < maxdam) {
            return `${locale(label)}${mindam}-${maxdam}`;
          } else {
            return mindam && `${locale(label)}${mindam}`;
          }
        } else {
          return formatStr(locale(label), mindam, maxdam);
        }
      }
    case 42: return `${locale(texta)}: ${plus(skillCalc(skill, calca, stats, level))}.${skillCalc(skill, calcb, stats, level)} ${locale(textb)}`;
    case 43:
      {
        const min = (skillCalc(skill, calca, stats, level) * 10) >> 8;
        const max = (skillCalc(skill, calcb, stats, level) * 10) >> 8;
        if (min < max) {
          return `${locale(texta)}${min / 10}-${max / 10}${locale(textb)}`;
        } else {
          return min && `${locale(texta)}${min / 10}${locale(textb)}`;
        }
      }
    case 47: return `${locale(texta)}${skillCalc(skill, calca, stats, level)}-${skillCalc(skill, calcb, stats, level)}`;
    case 49:
      {
        const min = skillFormula(skill, 'pdmn', stats, level);
        const max = skillFormula(skill, 'pdmx', stats, level);
        if (min < max) {
          return `${locale(texta)} ${plus(skillFormula(skill, 'pdmn', stats, level))}-${skillFormula(skill, 'pdmx', stats, level)}`;
        } else {
          return min && `${locale(texta)} ${plus(min)}`;
        }
      }
    case 50:
      {
        const min = skillFormula(skill, 'm1en', stats, level);
        const max = skillFormula(skill, 'm1ex', stats, level);
        if (min < max) {
          return `${locale(texta)}${min}-${max}`;
        } else {
          return min && `${locale(texta)}${min}`;
        }
      }
    case 51: return `${locale(texta).replace(/%d/g, skillCalc(skill, calca, stats, level))}`;
    case 52:
      {
        const min = skillCalc(skill, calca, stats, level);
        const max = skillCalc(skill, calcb, stats, level);
        if (min < max) {
          return `${locale(texta)}${min}-${max}${locale(textb)}`;
        } else {
          return min && `${locale(texta)}${min}${locale(textb)}`;
        }
      }
    case 56:
      return formatStr(Locale.strings.scrollbooktext, 1);
    case 57:
      {
        const len = (skillCalc(skill, calca, stats, level) * 10 / 25) | 0;
        return `${locale(texta)}${plus(len / 10)}${len === 10 ? Locale.strings.StrSkill15 : Locale.strings.StrSkill16}`;
      }
    case 59:
      {
        const min = skillCalc(skill, calca, stats, level);
        const max = skillCalc(skill, calcb, stats, level);
        if (min < max) {
          return `${locale(texta)}${min}-${max}${locale(textb)}`;
        } else {
          return min && `${locale(texta)}${min}${locale(textb)}`;
        }
      }
    case 61:
      {
        const prec = skillCalc(skill, calcb, stats, level);
        const value = (skillCalc(skill, calca, stats, level) * prec / 256) | 0;
        return `${locale(texta)}${value / prec}${locale(textb)}`;
      }
    case 62:
      {
        const min = skillCalc(skill, calca, stats, level);
        const max = skillCalc(skill, calcb, stats, level);
        if (min < max) {
          return `${locale(texta)}${locale(textb)}${min}-${max}`;
        } else {
          return min && `${locale(texta)}${locale(textb)}${min}`;
        }
      }
    case 63: return `${locale(texta)}: ${plus(skillCalc(skill, calca, stats, level))}% ${locale(textb)}`;
    case 66: return `${locale(texta).replace(/%d%%/g, skillCalc(skill, calca, stats, level) + '%')}`;
    case 67: return `${locale(texta)}: ${plus(skillCalc(skill, calca, stats, level))} ${locale(textb)}`;
    case 68: return `${skillCalc(skill, calca, stats, level)}${locale(texta)}${locale(textb)}`;
    case 70: return `${locale(texta)}${locale(textb)}${plus(skillCalc(skill, calca, stats, level))}`;
    case 71: return `${locale(texta)}: ${locale(textb).replace(/%d/g, skillCalc(skill, calca, stats, level))}`;
    case 72: return `${plus(skillCalc(skill, calca, stats, level))}/${skillCalc(skill, calcb, stats, level)} ${locale(texta)}`;
    case 73: return `${skillCalc(skill, calca, stats, level)}/${skillCalc(skill, calcb, stats, level)} ${locale(texta)}`;
    case 74:
      {
        const val = skillCalc(skill, calca, stats, level);
        return val && formatStr(locale(texta), val);
      }
    case 75: return formatStr(locale(texta), skillCalc(skill, calca, stats, level), skillCalc(skill, calcb, stats, level));
    case 76: return formatStr(locale(texta), locale(textb), skillCalc(skill, calca, stats, level));
    case 77: return formatStr(locale(texta), locale(textb), skillCalc(skill, calca, stats, level), skillCalc(skill, calcb, stats, level));
    // no default
    }
  }
  
  function addLine(...args) {
    const text = descFunc(...args);
    if (text) lines.push(...text.split('\n').map(txt => `\\xffc${color}${txt}`));
  }

  if (full) {
    if (desc.dsc2line1) lines.push('');
    for (let i = 5; i >= 1; --i) {
      addLine(desc[`dsc2line${i}`], desc[`dsc2texta${i}`], desc[`dsc2textb${i}`], desc[`dsc2calca${i}`], desc[`dsc2calcb${i}`], level || 1);
    }
  }

  if (desc.descline1) {
    lines.push('');
    if (level) {
      if (Data.info.legacyFormat) {
        lines.push(`\\xffc${color}${Locale.strings.StrSkill2}${level}${base_level || canLevel === 'special' ? '' : Locale.strings.StrSkill117}`);
      } else {
        lines.push(`\\xffc${color}${formatStr(base_level || canLevel === 'special' ? Locale.strings.StrSkill2 : Locale.strings.StrSkill117, level)}`);
      }
    } else {
      lines.push(`\\xffc${color}${Locale.strings.StrSkill17}`);
    }
    if (!full) {
      for (let i = 5; i >= 1; --i) {
        addLine(desc[`dsc2line${i}`], desc[`dsc2texta${i}`], desc[`dsc2textb${i}`], desc[`dsc2calca${i}`], desc[`dsc2calcb${i}`], level || 1);
      }  
    }
    for (let i = 6; i >= 1; --i) {
      addLine(desc[`descline${i}`], desc[`desctexta${i}`], desc[`desctextb${i}`], desc[`desccalca${i}`], desc[`desccalcb${i}`], level || 1);
    }
    if (level && base_level < 20 && full) {
      lines.push('');
      lines.push(`\\xffc${color}${Locale.strings.StrSkill1}`);
      for (let i = 6; i >= 1; --i) {
        addLine(desc[`descline${i}`], desc[`desctexta${i}`], desc[`desctextb${i}`], desc[`desccalca${i}`], desc[`desccalcb${i}`], level + 1);
      }
    }
  }

  if (full) {
    if (desc.dsc3line1) lines.push('');
    for (let i = 1; i <= 7; ++i) {
      addLine(desc[`dsc3line${i}`], desc[`dsc3texta${i}`], desc[`dsc3textb${i}`], desc[`dsc3calca${i}`], desc[`dsc3calcb${i}`], level || 1);
    }
  }

  return lines;
}
